import React, { useState } from "react";
import { getContactTransactionStatement, getTransactionStatement } from "../components/Apis/apis";
import { Dialog, CircularProgress } from "@mui/material";

const MailStatementDialog = ({ 
  open, 
  onClose, 
  contactId, 
  contactName,
  isRecordTab = false,
  filter,
  startDate,
  endDate
}) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [action, setAction] = useState("mail"); // 'mail' or 'download'
  
  // Local state for dates in case user wants to override
  const [customStartDate, setCustomStartDate] = useState(startDate ? new Date(startDate).toISOString().split('T')[0] : "");
  const [customEndDate, setCustomEndDate] = useState(endDate ? new Date(endDate).toISOString().split('T')[0] : "");

  const handleSend = async () => {
    try {
      setLoading(true);
      setMessage("");
      
      let response;
      if (isRecordTab) {
        let search = { notes: filter?.name };
        if (filter?.type === "expense") search["type"] = 1;
        else if (filter?.type === "income") search["type"] = 2;

        const params = {
          search: JSON.stringify(search),
          startDate: customStartDate ? new Date(customStartDate).toISOString() : undefined,
          endDate: customEndDate ? new Date(customEndDate).toISOString() : undefined,
          email: action === 'mail' ? (email || undefined) : undefined,
          showAll: true,
          action
        };
        if (action === 'download') {
           response = await getTransactionStatement({...params, responseType: 'blob'});
        } else {
           response = await getTransactionStatement(params);
        }
      } else {
        const params = {
          contactId,
          startDate: customStartDate ? new Date(customStartDate).toISOString() : undefined,
          endDate: customEndDate ? new Date(customEndDate).toISOString() : undefined,
          email: action === 'mail' ? (email || undefined) : undefined,
          showAll: true,
          action
        };
        if (action === 'download') {
           response = await getContactTransactionStatement({...params, responseType: 'blob'});
        } else {
           response = await getContactTransactionStatement(params);
        }
      }

      if (action === 'download') {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'Statement.pdf');
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        
        setMessage("Download started successfully.");
        setTimeout(() => {
          onClose();
          setMessage("");
        }, 1500);
      } else {
        if (response?.data?.status === 200 || response?.status === 200) {
          setMessage("Statement mailed successfully.");
          setTimeout(() => {
            onClose();
            setMessage("");
            setEmail("");
          }, 1500);
        } else {
          setMessage("Failed to send statement.");
        }
      }
    } catch (error) {
      console.error(error);
      setMessage(`Error ${action === 'download' ? 'downloading' : 'sending'} statement.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={!loading ? onClose : undefined} 
      maxWidth="xs" 
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: "var(--bg-secondary)",
          color: "var(--text-primary)",
          borderRadius: "20px",
          border: "1px solid var(--border-color)",
          boxShadow: "var(--glass-shadow)"
        }
      }}
    >
      <div style={{ padding: "32px 28px 24px" }}>
        <h4 style={{ 
          fontWeight: 800, 
          margin: "0 0 12px 0",
          fontSize: "1.4rem",
          letterSpacing: "-0.01em",
          color: "var(--text-primary)"
        }}>
          Export Statement
        </h4>
        <p style={{ 
          fontSize: "0.95rem", 
          color: "var(--text-secondary)", 
          lineHeight: 1.5,
          margin: "0 0 24px 0"
        }}>
          {isRecordTab 
            ? "Export your general account transaction statement." 
            : `Export the ledger statement for ${contactName}.`
          } 
        </p>

        <div style={{ display: "flex", gap: "12px", marginBottom: "24px" }}>
          <button
            onClick={() => setAction("mail")}
            style={{
              flex: 1,
              padding: "12px",
              borderRadius: "12px",
              border: `1px solid ${action === "mail" ? "var(--primary)" : "var(--border-color)"}`,
              backgroundColor: action === "mail" ? "var(--primary-glow)" : "var(--bg-surface)",
              color: action === "mail" ? "var(--primary)" : "var(--text-secondary)",
              fontWeight: 700,
              fontSize: "0.95rem",
              cursor: "pointer",
              transition: "all 0.2s"
            }}
          >
            ✉ Email
          </button>
          <button
            onClick={() => setAction("download")}
            style={{
              flex: 1,
              padding: "12px",
              borderRadius: "12px",
              border: `1px solid ${action === "download" ? "var(--primary)" : "var(--border-color)"}`,
              backgroundColor: action === "download" ? "var(--primary-glow)" : "var(--bg-surface)",
              color: action === "download" ? "var(--primary)" : "var(--text-secondary)",
              fontWeight: 700,
              fontSize: "0.95rem",
              cursor: "pointer",
              transition: "all 0.2s"
            }}
          >
            ↓ Download PDF
          </button>
        </div>

        <div style={{ marginBottom: "24px" }}>
          <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
            <div style={{ flex: 1 }}>
              <label style={{
                display: "block",
                fontSize: "0.75rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "rgba(255, 255, 255, 0.4)",
                marginBottom: "8px"
              }}>
                Start Date
              </label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                disabled={loading}
                style={{
                  width: "100%",
                  backgroundColor: "var(--bg-surface)",
                  border: "1px solid var(--border-color)",
                  borderRadius: "12px",
                  padding: "12px",
                  color: "var(--text-primary)",
                  fontSize: "0.95rem",
                  outline: "none",
                  transition: "border-color 0.2s"
                }}
                onFocus={(e) => e.target.style.borderColor = "var(--primary)"}
                onBlur={(e) => e.target.style.borderColor = "var(--border-color)"}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{
                display: "block",
                fontSize: "0.75rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "var(--text-muted)",
                marginBottom: "8px"
              }}>
                End Date
              </label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                disabled={loading}
                style={{
                  width: "100%",
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "12px",
                  padding: "12px",
                  color: "#fff",
                  fontSize: "0.95rem",
                  outline: "none",
                  transition: "border-color 0.2s"
                }}
                onFocus={(e) => e.target.style.borderColor = "var(--primary, #725DFF)"}
                onBlur={(e) => e.target.style.borderColor = "rgba(255, 255, 255, 0.1)"}
              />
            </div>
          </div>
          
          {action === "mail" && (
            <>
              <label style={{
                display: "block",
                fontSize: "0.75rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "var(--text-muted)",
                marginBottom: "8px"
              }}>
                Recipient Email
              </label>
              <input
                autoFocus
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                placeholder="Leave empty to send to your registered email"
                style={{
                  width: "100%",
                  backgroundColor: "var(--bg-surface)",
                  border: "1px solid var(--border-color)",
                  borderRadius: "12px",
                  padding: "14px 16px",
                  color: "var(--text-primary)",
                  fontSize: "0.95rem",
                  outline: "none",
                  transition: "border-color 0.2s, background-color 0.2s"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "var(--primary)";
                  e.target.style.backgroundColor = "var(--bg-surface-hover)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "var(--border-color)";
                  e.target.style.backgroundColor = "var(--bg-surface)";
                }}
              />
            </>
          )}
          {action === "download" && (
            <div style={{
              padding: "16px",
              backgroundColor: "var(--bg-surface)",
              borderRadius: "12px",
              border: "1px solid var(--border-color)",
              color: "var(--text-secondary)",
              fontSize: "0.9rem",
              lineHeight: 1.5,
              textAlign: "center"
            }}>
              A PDF file containing the statement will be downloaded directly to your device.
            </div>
          )}
          {message && (
            <div style={{ 
              marginTop: "12px", 
              fontSize: "0.85rem", 
              color: message.includes("success") ? "var(--success)" : "var(--danger)", 
              fontWeight: 600 
            }}>
              {message}
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", marginTop: "32px" }}>
          <button 
            onClick={onClose} 
            disabled={loading} 
            style={{
              backgroundColor: "transparent",
              color: "var(--text-secondary)",
              border: "none",
              fontWeight: 700,
              fontSize: "0.95rem",
              padding: "10px 20px",
              borderRadius: "10px",
              cursor: loading ? "not-allowed" : "pointer",
              transition: "color 0.2s, background-color 0.2s"
            }}
            onMouseEnter={(e) => !loading && (e.target.style.color = "var(--text-primary)", e.target.style.backgroundColor = "var(--bg-surface-hover)")}
            onMouseLeave={(e) => !loading && (e.target.style.color = "var(--text-secondary)", e.target.style.backgroundColor = "transparent")}
          >
            Cancel
          </button>
          <button 
            onClick={handleSend} 
            disabled={loading}
            style={{
              backgroundColor: "var(--primary)",
              color: "#fff",
              border: "none",
              fontWeight: 700,
              fontSize: "0.95rem",
              padding: "10px 24px",
              borderRadius: "10px",
              cursor: loading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "120px",
              transition: "transform 0.1s, opacity 0.2s",
              opacity: loading ? 0.7 : 1,
              boxShadow: "0 4px 12px var(--primary-glow)"
            }}
            onMouseDown={(e) => !loading && (e.target.style.transform = "scale(0.97)")}
            onMouseUp={(e) => !loading && (e.target.style.transform = "scale(1)")}
            onMouseLeave={(e) => !loading && (e.target.style.transform = "scale(1)")}
          >
            {loading ? <CircularProgress size={20} style={{ color: "#fff" }} /> : action === "mail" ? "Send Email" : "Download Now"}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default MailStatementDialog;
